import { useAppDispatch, useAppSelector } from 'app/hooks';
import PageHeader from 'components/PageHeader';
import { selectConfiguration } from 'features/configuration/configurationSlice';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import SideBarMenu from './components/SideBarMenu';
import CustomButton from 'styles/buttonStyled';
import CandidatesList from './components/CandidatesList';
import {
  getCandidateManagement,
  getCandidateManagementInterviewer,
  getListJob,
  sendMailOffer,
  sendMailInterview,
} from '../recruitmentAction';
import { useHistory } from 'react-router-dom';
import { Alert, Checkbox, Dropdown, Form, Input, Modal, Popover, Select, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { DownOutlined, ExportOutlined, FilterOutlined } from '@ant-design/icons';
import { getUser } from 'utils/auth';
import { isApproveOffer, isInterviewer, JobStatus } from '../jobs/components/types';
import { selectEmployee, setCheckRow } from 'features/employee/employeeSlice';
import { useTranslation } from 'react-i18next';
import { CustomButtonFilter } from 'features/employee/components/Functions';
import CandidateSimpleFilter from './components/CandidateSimpleFilter';
import { notificationToast } from 'components/notificationToast';
import recruitmentAiApi from 'api/recruitmentAiApi';
import styled from 'styled-components';
import ITVSearch from 'components/ITVSearch';
import BulkCVUploadModal from '../jobs/components/BulkCVUploadModal';
import axiosClient from 'api/axiosClient';
import moment from 'moment';
import CustomSelect from 'styles/inputSelectStyled';
import { selectRecruitmentData } from 'features/recruitment/recruitmentSlice';

const shouldUseAiCandidateSearch = (query: string) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return false;

  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedQuery);
  const isPhone = /^[+\d][\d\s().-]{5,}$/.test(normalizedQuery);
  if (isEmail || isPhone) return false;

  const words = normalizedQuery.split(/\s+/);
  const englishSignal =
    /\b(candidate|candidates|developer|engineer|senior|junior|experience|skills?|english|applied|years?|months?|days?)\b/i;
  const vietnameseSignal =
    /(ứng viên|lập trình|kỹ sư|kinh nghiệm|kỹ năng|tiếng anh|ứng tuyển|năm|tháng|ngày|trên|dưới|tối thiểu|ít nhất)/i;
  const roleOrSkillSignal =
    /\b(react|angular|vue|node|java|python|php|golang|flutter|ios|android|frontend|backend|fullstack|devops|qa|tester|product|designer)\b/i;
  const comparisonSignal = /(>=|<=|>|<|\d+\s*\+|hơn\s+\d+|at least|more than|less than)/i;

  if (words.length === 1) return roleOrSkillSignal.test(normalizedQuery);

  return (
    words.length > 3 ||
    englishSignal.test(normalizedQuery) ||
    vietnameseSignal.test(normalizedQuery) ||
    roleOrSkillSignal.test(normalizedQuery) ||
    comparisonSignal.test(query)
  );
};

enum HiringProcessStatusCandidate {
  APPLICATION_RECEIVED = 'Application Received',
  SHORTLISTED = 'Shortlisted',
  IN_PROGRESS = 'In Progress',
  JOB_OFFER = 'Job Offer',
  OFFER_ACCEPTED = 'Offer Accepted',
  OFFER_REJECTED = 'Offer Rejected',
  CLOSED = 'Closed',
}

const CandidatesManagement = () => {
  const { t } = useTranslation(['recruitment', 'employee']);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const menuStore = useAppSelector(selectConfiguration);
  const dataScreenManage = (menuStore?.dataScreenManage as any)?.result;
  const [titleHeader, setTitleHeader] = useState('');
  const [sizePage, setSizePage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchName, setSearchName] = useState('');
  const [targetJobCandidateId, setTargetJobCandidateId] = useState('');
  const [status, setStatus] = useState<HiringProcessStatusCandidate | undefined>();
  const [data, setData] = useState<any>([]);
  const isCheckAll = useAppSelector(selectEmployee).isCheckAll;
  const [isOpenSimpleFilter, setIsOpenSimpleFilter] = useState(false);
  const [manualFilter, setManualFilter] = useState<any>({});
  const [aiFilter, setAiFilter] = useState<any>({});
  const [aiSearchExplanation, setAiSearchExplanation] = useState('');
  const [visibleBulkCV, setVisibleBulkCV] = useState(false);
  const [candidateRefreshKey, setCandidateRefreshKey] = useState(0);
  const [exportVisible, setExportVisible] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportJobId, setExportJobId] = useState<string | undefined>(undefined);
  const [exportCandidates, setExportCandidates] = useState<any[]>([]);
  const [exportCandidatesLoading, setExportCandidatesLoading] = useState(false);
  const [exportSelectedIds, setExportSelectedIds] = useState<string[]>([]);
  const [exportFilterName, setExportFilterName] = useState('');
  const [exportFilterEmail, setExportFilterEmail] = useState('');
  const [exportFilterStage, setExportFilterStage] = useState<string | undefined>(undefined);
  const listJobApproved = useAppSelector(selectRecruitmentData)?.listJobApproved || [];

  const filter = useMemo(() => ({ ...manualFilter, ...aiFilter }), [aiFilter, manualFilter]);

  useEffect(() => {
    const state = location.state as any;
    if (state?.offerCandidate?.jobCandidateId) {
      setStatus(HiringProcessStatusCandidate.JOB_OFFER);
      setSearchName(state.offerCandidate.candidateName || '');
      setCurrentPage(1);
    }
    if (state?.successMessage) {
      notificationToast('success', state.successMessage, 5);
    }
    if (state?.successMessage || state?.offerCandidate) {
      history.replace({ ...location, state: {} });
    }
  }, [location.state, history, location]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const jobCandidateId = query.get('offerJobCandidateId');
    if (!jobCandidateId) return;

    setStatus(HiringProcessStatusCandidate.JOB_OFFER);
    setTargetJobCandidateId(jobCandidateId);
    setSearchName(query.get('offerCandidateName') || '');
    setCurrentPage(1);
    history.replace({ pathname: location.pathname, state: {} });
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    const temp = dataScreenManage
      ?.filter((item: any) => location.pathname === item.path)
      ?.map((item: any) => item.nameScreen);

    setTitleHeader(temp ? temp[0] : '');
  }, [dataScreenManage, location.pathname]);

  const callApi = () => {
    const query = {
      limit: sizePage,
      offset: currentPage,
      searchName,
      status,
      jobCandidateId: targetJobCandidateId || undefined,
      filter,
      //
    };
    if (isInterviewer()) {
      dispatch(getCandidateManagementInterviewer(query));
    } else {
      dispatch(getCandidateManagement(query));
    }
  };

  useEffect(() => {
    if (!isCheckAll) callApi();
  }, [
    dispatch,
    sizePage,
    currentPage,
    searchName,
    status,
    targetJobCandidateId,
    filter,
    candidateRefreshKey,
  ]);

  useEffect(() => {
    const timer = window.setInterval(() => setCandidateRefreshKey((value) => value + 1), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isCheckAll) {
      const query = { status };
      if (isInterviewer()) {
        dispatch(getCandidateManagementInterviewer(query));
      } else {
        dispatch(getCandidateManagement(query));
      }
    }
  }, [isCheckAll, status]);

  useEffect(() => {
    dispatch(setCheckRow([]));
  }, [status]);

  const menuItems = [
    {
      key: '1',
      label: t('recruitment:interview_invitation'),
      onClick: () => {
        dispatch(
          sendMailInterview({
            body: {
              jobCandidateIds: data,
              type: 'Interview',
            },
          }),
        );
      },
    },
    {
      key: '2',
      label: t('recruitment:interview_rejection'),
      onClick: () => {
        dispatch(
          sendMailOffer({
            body: {
              jobCandidateIds: data,
              status: 'rejected',
              type: 'Interview',
            },
          }),
        );
      },
    },
  ];

  const handleOpenSimpleFilter = (visible: any) => {
    setIsOpenSimpleFilter(visible);
  };

  useEffect(() => {
    if (exportVisible) {
      dispatch(getListJob({ jobRequestStatus: 'all' }));
    } else {
      // Reset export modal state when closed
      setExportJobId(undefined);
      setExportCandidates([]);
      setExportSelectedIds([]);
      setExportFilterName('');
      setExportFilterEmail('');
      setExportFilterStage(undefined);
    }
  }, [dispatch, exportVisible]);

  useEffect(() => {
    if (!exportJobId) {
      setExportCandidates([]);
      setExportSelectedIds([]);
      return;
    }
    const fetchCandidatesForJob = async () => {
      setExportCandidatesLoading(true);
      try {
        const res: any = await axiosClient.post('/recruitment/candidate/management', {
          jobRequisitionId: exportJobId,
          limit: 999,
          offset: 1,
        });
        const list = res?.data?.result || [];
        setExportCandidates(list);
        setExportSelectedIds(list.map((c: any) => c.id));
      } catch {
        setExportCandidates([]);
        setExportSelectedIds([]);
      } finally {
        setExportCandidatesLoading(false);
      }
    };
    fetchCandidatesForJob();
  }, [exportJobId]);

  const selectedJob = useMemo(
    () => listJobApproved.find((j: any) => j.id === exportJobId),
    [listJobApproved, exportJobId],
  );

  const exportStageOptions = useMemo(() => {
    const jobStages =
      selectedJob?.hiringProcess?.map((s: any) => s.name || s.status).filter(Boolean) || [];
    const candidateStages = exportCandidates.map((c: any) => c.stageName).filter(Boolean);
    return Array.from(new Set([...jobStages, ...candidateStages])) as string[];
  }, [selectedJob, exportCandidates]);

  const exportFilteredCandidates = exportCandidates.filter((c) => {
    const nameMatch =
      !exportFilterName ||
      (c.candidateName || '').toLowerCase().includes(exportFilterName.toLowerCase());
    const emailMatch =
      !exportFilterEmail || (c.email || '').toLowerCase().includes(exportFilterEmail.toLowerCase());
    const stageMatch = !exportFilterStage || c.stageName === exportFilterStage;
    return nameMatch && emailMatch && stageMatch;
  });

  const exportAllFiltered = exportFilteredCandidates.map((c: any) => c.id);
  const effectiveSelectedIds = exportAllFiltered.filter((id) => exportSelectedIds.includes(id));
  const isSelectAll =
    exportAllFiltered.length > 0 && exportAllFiltered.every((id) => exportSelectedIds.includes(id));
  const isIndeterminate =
    !isSelectAll && exportAllFiltered.some((id) => exportSelectedIds.includes(id));

  const handleExport = async () => {
    setExporting(true);
    try {
      const body: any = { jobRequisitionId: exportJobId || undefined };
      // Only send candidateIds matching the filtered selection
      if (exportJobId) {
        body.candidateIds = effectiveSelectedIds;
      }
      const res: any = await axiosClient.post('/recruitment/candidate/export', body, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Candidates_${moment().format('YYYY-MM-DD_HH-mm')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      notificationToast('success', t('recruitment:export_candidates_success'), 5);
      setExportVisible(false);
    } catch (error) {
      // error message is already shown by the axiosClient interceptor
    } finally {
      setExporting(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newIds = Array.from(new Set([...exportSelectedIds, ...exportAllFiltered]));
      setExportSelectedIds(newIds);
    } else {
      setExportSelectedIds(exportSelectedIds.filter((id) => !exportAllFiltered.includes(id)));
    }
  };

  const handleToggleCandidate = (id: string, checked: boolean) => {
    if (checked) {
      setExportSelectedIds((prev) => Array.from(new Set([...prev, id])));
    } else {
      setExportSelectedIds((prev) => prev.filter((sid) => sid !== id));
    }
  };

  const exportColumns: ColumnsType<any> = [
    {
      title: (
        <Checkbox
          checked={isSelectAll}
          indeterminate={isIndeterminate}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      width: 44,
      align: 'center',
      render: (_: any, record: any) => (
        <Checkbox
          checked={exportSelectedIds.includes(record.id)}
          onChange={(e) => handleToggleCandidate(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: t('recruitment:candidate_name'),
      dataIndex: 'candidateName',
      ellipsis: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      ellipsis: true,
    },
    {
      title: t('recruitment:job_stage'),
      dataIndex: 'stageName',
      width: 160,
      ellipsis: true,
      render: (stage: string) => stage || '-',
    },
  ];

  const handleCandidateSearch = async (value: string) => {
    const query = value.trim();
    setTargetJobCandidateId('');
    setCurrentPage(1);

    if (!query) {
      setSearchName('');
      setAiFilter({});
      setAiSearchExplanation('');
      return;
    }

    if (!shouldUseAiCandidateSearch(query)) {
      setAiFilter({});
      setAiSearchExplanation('');
      setSearchName(query);
      return;
    }

    const result = await recruitmentAiApi.parseCandidateSearch({
      prompt: query,
      context: {
        currentStatus: status,
        classifyIntent: true,
        allowedIntents: ['keyword', 'ai'],
        allowedFilters: [
          'candidateName',
          'email',
          'phone',
          'positionName',
          'appliedDate',
          'result',
          'stageName',
          'createdAt',
          'salaryOffer',
          'offerStatus',
          'expectedStartDate',
          'location',
          'interviewerStatus',
          'candidateInterviewStatus',
          'interviewDateAndTime',
          'offerBy',
          'source',
          'skills',
          'languages',
          'minimumYearsExperience',
        ],
      },
      options: { language: t('recruitment:ai_response_language') },
    });

    const filters = result.filters || {};
    const hasAiFilters = Object.keys(filters).length > 0;
    if (result.intent === 'keyword' || !hasAiFilters) {
      setAiFilter({});
      setAiSearchExplanation('');
      setSearchName(result.keyword?.trim() || query);
      return;
    }

    setSearchName('');
    setAiFilter(filters);
    setAiSearchExplanation(result.explanation || query);
  };

  return (
    <>
      <PageHeader title={titleHeader} />

      <div>
        {(!isInterviewer() || isApproveOffer()) && (
          <SideBarMenu
            status={status}
            setStatus={setStatus as any}
            onChange={() => {
              setTargetJobCandidateId('');
              setManualFilter({});
              setAiFilter({});
              setAiSearchExplanation('');
            }}
          />
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            width: '100%',
            overflow: 'auto',
          }}
        >
          <Toolbar>
            <ActionGroup>
              {(isApproveOffer() || getUser()?.permissions?.includes('RECRUIT_JOB_CREATED')) && (
                <>
                  {!isInterviewer() && (
                    <>
                      <CustomButton
                        onClick={() => history.push(`/recruitment/candidates/detail/draft/create`)}
                      >
                        {t('recruitment:create_candidate')}
                      </CustomButton>
                      <CustomButton aType="outline" onClick={() => setVisibleBulkCV(true)}>
                        {t('recruitment:bulk_cv_button')}
                      </CustomButton>
                    </>
                  )}
                  {status === HiringProcessStatusCandidate.IN_PROGRESS ? (
                    <Dropdown disabled={!data?.length} menu={{ items: menuItems }}>
                      <CustomButton aType="primary">
                        {t('recruitment:send_interview_email')} <DownOutlined />
                      </CustomButton>
                    </Dropdown>
                  ) : null}
                </>
              )}
              <Popover
                placement="bottom"
                content={
                  <CandidateSimpleFilter
                    filter={manualFilter}
                    status={status}
                    onFilterChange={(value: any) => {
                      setManualFilter(value);
                      setCurrentPage(1);
                    }}
                  />
                }
                trigger="click"
                open={isOpenSimpleFilter}
                onOpenChange={handleOpenSimpleFilter}
              >
                <CustomButtonFilter>
                  <FilterOutlined />
                  {t('employee:filter.title')}
                  <DownOutlined />
                </CustomButtonFilter>
              </Popover>
              <CustomButton
                aType="outline"
                icon={<ExportOutlined />}
                onClick={() => setExportVisible(true)}
              >
                {t('action:export')}
              </CustomButton>
            </ActionGroup>
            <SmartCandidateSearch
              debounceWait={800}
              placeholder={t('recruitment:candidate_search_smart_placeholder')}
              handleSearch={handleCandidateSearch}
            />
          </Toolbar>
          {aiSearchExplanation && (
            <SearchFeedback>
              <Alert
                showIcon={false}
                type="info"
                closable
                message={t('recruitment:ai_candidate_search_applied')}
                description={aiSearchExplanation}
                onClose={() => {
                  setAiSearchExplanation('');
                  setAiFilter({});
                  setCurrentPage(1);
                }}
              />
            </SearchFeedback>
          )}
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <CandidatesList
              status={status}
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              setSizePage={setSizePage}
              sizePage={sizePage}
              callBack={callApi}
              setData={setData}
              filter={filter}
            />
          </div>
        </div>
      </div>
      <BulkCVUploadModal
        visible={visibleBulkCV}
        onCancel={() => setVisibleBulkCV(false)}
        onImported={() => {
          setCurrentPage(1);
          setCandidateRefreshKey((value) => value + 1);
        }}
      />
      <Modal
        title={t('recruitment:export_candidates')}
        visible={exportVisible}
        onCancel={() => setExportVisible(false)}
        onOk={handleExport}
        okText={
          exportJobId
            ? `${t('action:export')} (${effectiveSelectedIds.length}/${
                exportFilteredCandidates.length
              })`
            : t('action:export')
        }
        okButtonProps={{ disabled: exportJobId ? effectiveSelectedIds.length === 0 : false }}
        cancelText={t('action:cancel')}
        confirmLoading={exporting}
        destroyOnClose
        width={720}
      >
        <Form layout="vertical">
          <Form.Item label={t('recruitment:select_job')} style={{ marginBottom: 12 }}>
            <CustomSelect
              allowClear
              showSearch
              optionFilterProp="children"
              optionLabelProp="label"
              placeholder={t('recruitment:all_jobs')}
              value={exportJobId}
              onChange={(value: unknown) => {
                setExportJobId(value as string);
                setExportFilterName('');
                setExportFilterEmail('');
                setExportFilterStage(undefined);
              }}
            >
              {listJobApproved.map((job: any) => {
                const name = job?.jobTemplate?.positionName || job?.jobName || job?.id;
                const jobId = job?.jobId || job?.id?.slice(0, 8);
                const status = job?.status || job?.jobRequestStatus || '';
                return (
                  <Select.Option key={job.id} value={job.id} label={name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {name}
                      </span>
                      <span style={{ color: '#aaa', fontSize: 11, whiteSpace: 'nowrap' }}>
                        #{jobId} · {status}
                      </span>
                    </div>
                  </Select.Option>
                );
              })}
            </CustomSelect>
          </Form.Item>

          {exportJobId && (
            <Spin spinning={exportCandidatesLoading}>
              <ExportFilterRow>
                <CustomSelect
                  allowClear
                  placeholder={t('recruitment:filter_by_stage')}
                  value={exportFilterStage}
                  onChange={(value: unknown) => setExportFilterStage(value as string | undefined)}
                  style={{ flex: '1 1 180px' }}
                >
                  {exportStageOptions.map((stage) => (
                    <Select.Option key={stage} value={stage}>
                      {stage}
                    </Select.Option>
                  ))}
                </CustomSelect>
                <Input
                  placeholder={t('recruitment:candidate_name')}
                  value={exportFilterName}
                  onChange={(e) => setExportFilterName(e.target.value)}
                  allowClear
                  style={{ flex: '1 1 160px' }}
                />
                <Input
                  placeholder="Email"
                  value={exportFilterEmail}
                  onChange={(e) => setExportFilterEmail(e.target.value)}
                  allowClear
                  style={{ flex: '1 1 180px' }}
                />
              </ExportFilterRow>
              <Table
                size="small"
                rowKey="id"
                columns={exportColumns}
                dataSource={exportFilteredCandidates}
                pagination={{ pageSize: 8, showSizeChanger: false, size: 'small' }}
                scroll={{ y: 280 }}
                locale={{ emptyText: t('recruitment:no_candidates_found') }}
                style={{ marginTop: 10 }}
              />
              <ExportSummary>
                {t('recruitment:export_selected_summary', {
                  selected: effectiveSelectedIds.length,
                  total: exportFilteredCandidates.length,
                })}
              </ExportSummary>
            </Spin>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default CandidatesManagement;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SmartCandidateSearch = styled(ITVSearch)`
  width: 420px;

  @media (max-width: 760px) {
    width: 100%;
  }
`;

const SearchFeedback = styled.div`
  display: flex;
  justify-content: flex-end;

  .ant-alert {
    width: min(100%, 620px);
  }
`;

const ExportFilterRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
`;

const ExportSummary = styled.div`
  margin-top: 6px;
  color: #888;
  font-size: 12px;
  text-align: right;
`;
